import { type InferRequest, invalidateTypedQueries, useApiClient } from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'
import { MoonFilled, SunFilled, WarningOutlined } from '@ant-design/icons'
import { Texto } from '@gravitate-js/excalibrr'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ICellRendererParams } from 'ag-grid-community'
import { Flex, Popover, Spin, message } from 'antd'
import { type HTMLAttributes, type ReactNode, memo, useMemo, useState } from 'react'
import { useSchedulePageFilters } from '../Context/Filters/SchedulePageFilterContext'
import { useScheduleItems } from '../Context/ScheduleItems/ScheduleItemsContext'
import { type ScheduleItemWithConflicts, getDayKey } from '../Context/ScheduleItems/scheduleItemStore'
import {
  EquipmentConflictsForm,
  type EquipmentConflictsFormInit,
} from '../Forms/EquipmentConflicts/EquipmentConflictsForm'
import { colDefIdToDay } from '../utils/getDefaultShift'

type Data = components['schemas']['TractorView']

const ShiftItemContent = ({ icon, title, status }: { icon?: ReactNode; title: ReactNode; status?: ReactNode }) => {
  const { datePickerType } = useSchedulePageFilters()
  if (datePickerType === 'month') {
    return (
      <Flex align='center' style={{ width: '100%', height: '100%' }} justify='center'>
        {icon}
      </Flex>
    )
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0px 4px',
        gap: '4px',
        minWidth: '0px',
        maxWidth: '100%',
        width: '100%',
      }}
    >
      {icon}
      <Texto className='text-truncate' style={{ color: 'inherit' }}>
        {title}
      </Texto>
      <div style={{ marginLeft: 'auto' }}>{status}</div>
    </div>
  )
}

const ShiftItem = ({
  values,
  shiftType,
  isPending,
  ...rest
}: {
  values: ScheduleItemWithConflicts[]
  isPending: boolean
  shiftType: 'am' | 'pm'
} & HTMLAttributes<HTMLDivElement>) => {
  const driverName = values.length > 1 ? `${values.length} Drivers` : (values[0].driver_name ?? 'Unknown Driver')

  const errors = values.flatMap((x) => x.errors)

  const errorText = errors.some((x) => x.type === 'InactiveTractor')
    ? 'Inactive'
    : errors.some((x) => x.type === 'OutOfServiceTractor')
      ? 'Out of Service'
      : errors.length
        ? 'Conflict'
        : undefined

  return (
    <div {...rest} className={`shift-cell tractor ${errors.length ? 'error' : shiftType}`}>
      <ShiftItemContent
        icon={
          isPending ? (
            <Spin size='small' />
          ) : errors.length ? (
            <WarningOutlined />
          ) : shiftType === 'am' ? (
            <SunFilled />
          ) : (
            <MoonFilled />
          )
        }
        title={driverName}
        status={
          errorText ?? (
            <Texto style={{ flexShrink: 0 }} appearance='hint' category='label'>
              {`${values[0].start.format('h:mm A')} - ${values[0].end.format('h:mm A z')}`}
            </Texto>
          )
        }
      />
    </div>
  )
}

type ShiftItemProps = { value: ScheduleItemWithConflicts[]; shiftType: 'am' | 'pm'; data: Data; day?: string }
const ShiftItems = ({ value, shiftType, data }: ShiftItemProps) => {
  const values = useMemo(() => value.filter((x) => x.shift === shiftType), [value, shiftType])
  const [open, setOpen] = useState(false)
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { mutateAsync: create } = apiClient.useMutation('post', '/driver/create_event_many')
  const { mutateAsync: deleteMany } = apiClient.useMutation('post', '/driver/delete_event_many', {
    onSuccess: (resp: any) => resp.detail && message.warning(resp.detail?.message),
  })
  const { mutateAsync: onSubmit, isPending } = useMutation({
    mutationFn: ({ items, removedItemIds }: EquipmentConflictsFormInit) => {
      const createReq = items?.length
        ? create({
            body: items.map((item) => ({
              id: item.event_id,
              driver: item.carrier_id
                ? {}
                : {
                    id: item.driver_or_carrier_id,
                  },
              carrier_id: item.carrier_id,
              carrier_resource_num: item.carrier_resource_num,
              date: item.date ?? '',
              start_date: item.shift_start_utc ?? '',
              end_date: item.shift_end_utc ?? '',
              depot: item.depot_name,
              tractor_id: item.tractor?.value ?? undefined,
              trailer_id: item.trailer?.value ?? undefined,
              type: item.shift === 'am' ? 'AM Shift' : item.shift === 'pm' ? 'PM Shift' : (item.shift ?? ''),
              rate_type: 'scheduled',
              timezone: undefined as unknown as InferRequest<'/driver/create_event'>['timezone'],
              // biome-ignore lint/suspicious/noExplicitAny: api shouldn't require this
              extra_data: undefined as any,
            })),
          })
        : Promise.resolve()

      const deleteReq = removedItemIds?.length
        ? deleteMany({ body: removedItemIds.map((id) => ({ id })) })
        : Promise.resolve()

      return Promise.all([createReq, deleteReq])
    },
    onSuccess: () => setOpen(false),
    onSettled: () => invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview'),
  })

  if (!values.length) return <div className='shift-cell no-shift' />

  return (
    <Popover
      trigger='click'
      open={open}
      onOpenChange={setOpen}
      destroyTooltipOnHide
      content={
        <EquipmentConflictsForm
          onSubmit={onSubmit}
          loading={isPending}
          onClose={() => setOpen(false)}
          values={values}
          shiftType={shiftType}
          tractor={data}
        />
      }
    >
      <ShiftItem isPending={isPending} values={values} shiftType={shiftType} />
    </Popover>
  )
}

function _DayCellRenderer({ data, colDef }: ICellRendererParams<Data>) {
  const { search } = useScheduleItems()
  const value = useMemo(
    () =>
      search(
        { index: 'tractor', value: data?.id ?? '' },
        { index: 'day', value: getDayKey(colDefIdToDay(colDef?.colId)) }
      ),
    [search, data?.id, colDef?.colId]
  )

  if (!data) return null
  return (
    <Flex vertical>
      <ShiftItems value={value ?? []} shiftType='am' data={data} day={colDef?.colId} />
      <hr
        style={{ width: '100%', padding: 0, margin: 0, height: '1px', backgroundColor: 'var(--gray-200)', border: 0 }}
      />
      <ShiftItems value={value ?? []} shiftType='pm' data={data} day={colDef?.colId} />
    </Flex>
  )
}

export const DayCellRenderer = memo(_DayCellRenderer)
